import { type FormEvent } from "react";
import { CollapsibleSection } from "../../shared/CollapsibleSection";
import { TagInput } from "../../shared/TagInput";
import type { QmForm, QmPerson, QmReferenz } from "./state";

interface Props {
  form: QmForm;
  loading: boolean;
  onChange: (patch: Partial<QmForm>) => void;
  onSubmit: () => void;
}

const EMPTY_PERSON: QmPerson = {
  name_funktion: "",
  qualifikation: "",
  erfahrung: "",
  sprachen: "",
};

const EMPTY_REFERENZ: QmReferenz = {
  ziel: "",
  branche: "",
  zeitraum: "",
  rolle: "",
  ergebnis: "",
};

export function Step2Form({ form, loading, onChange, onSubmit }: Props) {
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading) return;
    onSubmit();
  }

  function updatePerson(idx: number, patch: Partial<QmPerson>) {
    onChange({
      personen: form.personen.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    });
  }
  function removePerson(idx: number) {
    onChange({ personen: form.personen.filter((_, i) => i !== idx) });
  }
  function addPerson() {
    onChange({ personen: [...form.personen, { ...EMPTY_PERSON }] });
  }

  function updateRef(idx: number, patch: Partial<QmReferenz>) {
    onChange({
      referenzen: form.referenzen.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });
  }
  function removeRef(idx: number) {
    onChange({ referenzen: form.referenzen.filter((_, i) => i !== idx) });
  }
  function addRef() {
    onChange({ referenzen: [...form.referenzen, { ...EMPTY_REFERENZ }] });
  }

  return (
    <section className="wizard-section" data-qm-step="2">
      <h2 className="form-title">QM Handbuch: Unternehmensdaten eingeben</h2>
      <p className="form-description">
        Fülle die Felder aus. Der Agent erstellt daraus ein vollständiges QM-Handbuch.
        Mit dem Minimal-Set (A + B + Kurzprofil) kannst du sofort starten.
      </p>
      <form className="report-form" onSubmit={submit}>
        <CollapsibleSection title="A – Stammdaten und Positionierung" defaultOpen>
          <div className="form-row">
            <label htmlFor="qm-unternehmensname">Unternehmensname und Rechtsform *</label>
            <input
              id="qm-unternehmensname"
              type="text"
              required
              placeholder="z. B. Mustermann Consulting GmbH"
              value={form.unternehmensname}
              onChange={(e) => onChange({ unternehmensname: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label htmlFor="qm-sitz">Sitz, vollständige Adresse *</label>
            <input
              id="qm-sitz"
              type="text"
              required
              placeholder="z. B. Musterstraße 1, 80331 München"
              value={form.sitzAdresse}
              onChange={(e) => onChange({ sitzAdresse: e.target.value })}
            />
          </div>
          <div className="form-inline-group">
            <div className="form-row form-row-inline">
              <label htmlFor="qm-gruendung">Gründung *</label>
              <input
                id="qm-gruendung"
                type="text"
                required
                placeholder="z. B. März 2018"
                value={form.gruendung}
                onChange={(e) => onChange({ gruendung: e.target.value })}
              />
            </div>
            <div className="form-row form-row-inline">
              <label htmlFor="qm-mitarbeitende">Mitarbeitende (Anzahl, Rollen)</label>
              <input
                id="qm-mitarbeitende"
                type="text"
                placeholder="z. B. 3 Berater, 1 Werkstudent"
                value={form.mitarbeitende}
                onChange={(e) => onChange({ mitarbeitende: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <label htmlFor="qm-geschaeftsfuehrung">Geschäftsführung / Inhaber *</label>
            <input
              id="qm-geschaeftsfuehrung"
              type="text"
              required
              placeholder="z. B. Max Mustermann, Geschäftsführer"
              value={form.geschaeftsfuehrung}
              onChange={(e) => onChange({ geschaeftsfuehrung: e.target.value })}
            />
          </div>
          <div className="form-row form-row-full">
            <label htmlFor="qm-kurzprofil">Kurzprofil – strategische Positionierung (2 Sätze) *</label>
            <textarea
              id="qm-kurzprofil"
              rows={3}
              required
              placeholder="Wofür steht das Unternehmen strategisch? Welchen Nutzen liefert es?"
              value={form.kurzprofil}
              onChange={(e) => onChange({ kurzprofil: e.target.value })}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="B – Beratungsfelder (Priorität 1)" defaultOpen>
          <div className="form-row form-row-full">
            <label>Kern-Beratungsfelder (3–6) *</label>
            <TagInput
              values={form.kernfelder}
              onChange={(kernfelder) => onChange({ kernfelder })}
              placeholder="Feld eingeben, Enter drücken… z. B. Strategie"
            />
            <small className="form-hint">
              z. B. Strategie, Prozessmanagement, Digitalisierung, Vertrieb, Organisation, Förderung
            </small>
          </div>
          <div className="form-row form-row-full">
            <label>Ergänzende Umsetzungsleistungen (optional)</label>
            <TagInput
              values={form.umsetzung}
              onChange={(umsetzung) => onChange({ umsetzung })}
              placeholder="Leistung eingeben, Enter drücken…"
            />
            <small className="form-hint">z. B. Tool-Setup, Workshops, Templates, Projektleitung</small>
          </div>
          <div className="form-row form-row-full">
            <label htmlFor="qm-nicht-angeboten">Was wird ausdrücklich nicht angeboten</label>
            <textarea
              id="qm-nicht-angeboten"
              rows={2}
              placeholder="z. B. Softwareentwicklung, IT-Administration, Steuerberatung"
              value={form.nichtAngeboten}
              onChange={(e) => onChange({ nichtAngeboten: e.target.value })}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="C – Zielgruppen und Branchenfokus">
          <div className="form-row form-row-full">
            <label>Hauptzielgruppen</label>
            <TagInput
              values={form.zielgruppen}
              onChange={(zielgruppen) => onChange({ zielgruppen })}
              placeholder="Zielgruppe eingeben, Enter drücken…"
            />
            <small className="form-hint">
              z. B. KMU, Startups, Handwerk, Industrie, Dienstleister
            </small>
          </div>
          <div className="form-row">
            <label htmlFor="qm-branchenfokus">Branchenfokus (falls vorhanden)</label>
            <input
              id="qm-branchenfokus"
              type="text"
              placeholder="z. B. Gesundheitswesen, Maschinenbau oder leer lassen"
              value={form.branchenfokus}
              onChange={(e) => onChange({ branchenfokus: e.target.value })}
            />
          </div>
          <div className="form-row form-row-full">
            <label>Typische Projektanlässe</label>
            <TagInput
              values={form.anlaesse}
              onChange={(anlaesse) => onChange({ anlaesse })}
              placeholder="Anlass eingeben, Enter drücken…"
            />
            <small className="form-hint">
              z. B. Wachstum, Effizienz, neue Märkte, Krisen, Nachfolge, Digitalisierung
            </small>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="D – Team, Qualifikation und Referenzen">
          <p className="form-hint form-hint-block">
            Schlüsselpersonen (max. 5): Je Person Name, Funktion, Abschlüsse/Zertifikate,
            Berufserfahrung und ggf. Sprachen.
          </p>
          {form.personen.map((person, idx) => (
            <div className="repeatable-block" key={`person-${idx}`}>
              <div className="repeatable-block-header">
                <strong>Person {idx + 1}</strong>
                <button
                  type="button"
                  className="btn-remove-block"
                  title="Person entfernen"
                  onClick={() => removePerson(idx)}
                >
                  ×
                </button>
              </div>
              <div className="form-row">
                <label>Name und Funktion</label>
                <input
                  type="text"
                  placeholder="z. B. Max Mustermann, Senior Consultant"
                  value={person.name_funktion}
                  onChange={(e) => updatePerson(idx, { name_funktion: e.target.value })}
                />
              </div>
              <div className="form-row form-row-full">
                <label>Qualifikation / Zertifikate</label>
                <textarea
                  rows={2}
                  value={person.qualifikation}
                  onChange={(e) => updatePerson(idx, { qualifikation: e.target.value })}
                />
              </div>
              <div className="form-row form-row-full">
                <label>Berufserfahrung</label>
                <textarea
                  rows={2}
                  value={person.erfahrung}
                  onChange={(e) => updatePerson(idx, { erfahrung: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Sprachen</label>
                <input
                  type="text"
                  placeholder="z. B. Deutsch, Englisch"
                  value={person.sprachen}
                  onChange={(e) => updatePerson(idx, { sprachen: e.target.value })}
                />
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addPerson}>
            + Person hinzufügen
          </button>

          <hr className="form-divider" />

          <p className="form-hint form-hint-block">
            Referenzprojekte (3–10, ggf. anonymisiert): Je Projekt Ziel, Branche, Zeitraum,
            Rolle und Ergebnis.
          </p>
          {form.referenzen.map((ref, idx) => (
            <div className="repeatable-block" key={`ref-${idx}`}>
              <div className="repeatable-block-header">
                <strong>Referenzprojekt {idx + 1}</strong>
                <button
                  type="button"
                  className="btn-remove-block"
                  title="Referenz entfernen"
                  onClick={() => removeRef(idx)}
                >
                  ×
                </button>
              </div>
              <div className="form-row form-row-full">
                <label>Ziel</label>
                <textarea
                  rows={2}
                  value={ref.ziel}
                  onChange={(e) => updateRef(idx, { ziel: e.target.value })}
                />
              </div>
              <div className="form-inline-group">
                <div className="form-row form-row-inline">
                  <label>Branche</label>
                  <input
                    type="text"
                    value={ref.branche}
                    onChange={(e) => updateRef(idx, { branche: e.target.value })}
                  />
                </div>
                <div className="form-row form-row-inline">
                  <label>Zeitraum</label>
                  <input
                    type="text"
                    placeholder="z. B. 2024 – 2025"
                    value={ref.zeitraum}
                    onChange={(e) => updateRef(idx, { zeitraum: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Rolle</label>
                <input
                  type="text"
                  value={ref.rolle}
                  onChange={(e) => updateRef(idx, { rolle: e.target.value })}
                />
              </div>
              <div className="form-row form-row-full">
                <label>Ergebnis</label>
                <textarea
                  rows={2}
                  value={ref.ergebnis}
                  onChange={(e) => updateRef(idx, { ergebnis: e.target.value })}
                />
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addRef}>
            + Referenzprojekt hinzufügen
          </button>
        </CollapsibleSection>

        <CollapsibleSection title="E – Personal, Weiterentwicklung und Infrastruktur">
          {[
            { id: "personalgewinnung", label: "Personalgewinnung", placeholder: "Wie werden neue Mitarbeitende gefunden, geprüft und eingebunden?" },
            { id: "weiterbildung", label: "Weiterbildung", placeholder: "Formate, Quellen, Häufigkeit, Themen" },
            { id: "kapazitaeten", label: "Kapazitäten", placeholder: "Standard-Verfügbarkeit, Reaktionszeiten, Engpass-Handling" },
            { id: "infrastrukturTools", label: "Infrastruktur und Tools", placeholder: "Projektmanagement, Kollaboration, Dateiablage, Datenschutz, Kommunikation" },
            { id: "netzwerkePartner", label: "Netzwerke und Partner", placeholder: "Verbände, Hochschulen, Freelancer, Kanzleien, IT-Partner" },
          ].map((field) => (
            <div className="form-row form-row-full" key={field.id}>
              <label htmlFor={`qm-${field.id}`}>{field.label}</label>
              <textarea
                id={`qm-${field.id}`}
                rows={2}
                placeholder={field.placeholder}
                value={form[field.id as keyof QmForm] as string}
                onChange={(e) => onChange({ [field.id]: e.target.value } as Partial<QmForm>)}
              />
            </div>
          ))}
        </CollapsibleSection>

        <CollapsibleSection title="F – Beratungsprozess">
          {[
            { id: "erstkontakt", label: "Erstkontakt", placeholder: "Typischer Ablauf, Kanäle, welche Infos werden abgefragt" },
            { id: "bedarfsermittlung", label: "Bedarfsermittlung", placeholder: "Methoden, Dokumente, Workshops, Analysen" },
            { id: "auftragsklarung", label: "Auftragsklärung", placeholder: "Angebot, Vertrag, Scope, Rollen, Termine" },
            { id: "durchfuehrung", label: "Durchführung", placeholder: "Meilensteine, Jour-Fixe, Zwischenstände, Qualitätssicherung" },
            { id: "dokumentation", label: "Dokumentation", placeholder: "Deliverables, Protokolle, Abschlussbericht" },
            { id: "feedbackNachbetreuung", label: "Feedback und Nachbetreuung", placeholder: "Wie wird Wirkung geprüft, Follow-ups" },
          ].map((field) => (
            <div className="form-row form-row-full" key={field.id}>
              <label htmlFor={`qm-${field.id}`}>{field.label}</label>
              <textarea
                id={`qm-${field.id}`}
                rows={2}
                placeholder={field.placeholder}
                value={form[field.id as keyof QmForm] as string}
                onChange={(e) => onChange({ [field.id]: e.target.value } as Partial<QmForm>)}
              />
            </div>
          ))}
        </CollapsibleSection>

        <CollapsibleSection title="G – Qualitätsmanagement intern">
          <div className="form-row form-row-full">
            <label htmlFor="qm-qualitaetsziele">Qualitätsziele (3–6 messbare Ziele)</label>
            <textarea
              id="qm-qualitaetsziele"
              rows={3}
              placeholder="z. B. Kundenzufriedenheit > 85 %, Meilensteine pünktlich > 90 %, Rückmeldung innerhalb 24h"
              value={form.qualitaetsziele}
              onChange={(e) => onChange({ qualitaetsziele: e.target.value })}
            />
          </div>
          {[
            { id: "interneKontrollen", label: "Interne Qualitätskontrollen", placeholder: "Vier-Augen-Prinzip, Review-Rhythmus, Checklisten" },
            { id: "beschwerdenAbweichungen", label: "Beschwerden und Abweichungen", placeholder: "Wer nimmt an, Fristen, Dokumentation" },
            { id: "kvpLessonsLearned", label: "KVP und Lessons Learned", placeholder: "Wie oft, in welchem Format, wo wird es festgehalten?" },
            { id: "risikenChancen", label: "Risiken und Chancen", placeholder: "Typische Projektrisiken und wie damit umgegangen wird" },
            { id: "interneUeberpruefung", label: "Interne Überprüfung", placeholder: "Auditähnliche Checks, Management-Review, Frequenz" },
          ].map((field) => (
            <div className="form-row form-row-full" key={field.id}>
              <label htmlFor={`qm-${field.id}`}>{field.label}</label>
              <textarea
                id={`qm-${field.id}`}
                rows={2}
                placeholder={field.placeholder}
                value={form[field.id as keyof QmForm] as string}
                onChange={(e) => onChange({ [field.id]: e.target.value } as Partial<QmForm>)}
              />
            </div>
          ))}
        </CollapsibleSection>

        <p className="form-hint form-hint-block">
          <strong>Minimal-Set:</strong> Für einen sofortigen Start reichen Sektion A
          (Stammdaten) und Sektion B (Beratungsfelder). Alle weiteren Felder können
          später ergänzt werden.
        </p>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Wird geladen…" : "QM Handbuch generieren"}
          </button>
        </div>
      </form>
    </section>
  );
}
